/**
 * Primeval.js - Fetches dynamic session data from globals.json
 *
 * Static page config (env, locale) comes from page_config.js
 * Dynamic session data (user, tracking, csrf) comes from this globals.json fetch
 *
 * This separation allows:
 * - Static config to be parsed synchronously from server-rendered JSON
 * - Dynamic data to be fetched asynchronously per-session
 */

import { getPageConfig } from './page_config';
import { debug } from '../../../app/javascript/utils/debug';

/**
 * Fetch dynamic globals from the server
 * Updates window globals with session-specific data
 *
 * @returns {Promise<Object>} The globals data
 */
export const getGlobals = async () => {
  const validLocales = ['en-US', 'en-CA'];
  const config = getPageConfig();

  let paths = window.location.pathname.split('/');
  const locale = paths[1];
  let shouldRedirectToLocale = false;

  // Validate locale - must be en-US or en-CA
  if (!validLocales.includes(locale)) {
    window.ErrorReporter?.warn(locale + ' is not a supported locale');
    shouldRedirectToLocale = true;
  }

  // In embedded contexts (e.g., CRM preview pane), skip globals.json
  if (config.isCrm || window.location.pathname.indexOf('/preview') !== -1) {
    return {
      pageCsrf: window.pageCsrf,
      RMTREV: window.RMTREV,
      current_user: window.current_user,
      tracVis: window.tracVis,
      locale: locale || 'en-US',
      shouldRedirectToLocale
    };
  }

  // Build request data
  const data = {
    landing_page: window.location.href,
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    apparent_locale: locale
  };

  // Include referrer if present
  if (document.referrer.length > 0) {
    data.referrer = document.referrer;
  }

  // Only fetch if locale is valid
  if (!shouldRedirectToLocale) {
    const params = new URLSearchParams();
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
        params.append(key, String(data[key]));
      }
    });
    // Cache busting to avoid page cache collisions
    params.append('cb', `${Date.now()}`);

    const globalsUrl = `/${locale}/globals.json?${params.toString()}`;
    debug('primeval', `Fetching globals.json: ${globalsUrl.split('?')[0]}`);

    try {
      const res = await fetch(globalsUrl, {
        method: 'GET',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      let responseJson = null;
      try {
        responseJson = await res.json();
      } catch (e) {
        debug.warn('primeval', 'globals.json returned non-JSON payload');
      }

      if (res.status === 200 && responseJson) {
        // Update window globals with dynamic session data
        window.pageCsrf = responseJson.pageCsrf;
        window.RMTREV = responseJson.RMTREV;
        window.current_user = responseJson.current_user;
        window.$crisp = responseJson.$crisp;
        window.tracVis = responseJson.tracVis;
        window.locale = responseJson.locale || 'en-US';

        // Update cart badge in navbar
        const cartQty = responseJson.current_user?.cartQty || 0;
        document.querySelectorAll('.cart-count').forEach(el => {
          el.textContent = cartQty;
        });

        // Update tracking globals
        const tracking = responseJson.tracking || {};
        window.visitorCountry = tracking.visitor_country || 'US';
        window.visitorRegion = tracking.visitor_region || null;
        window.isGdprCountry = tracking.gdpr_country || false;
        window.trackingConfig = tracking;

        debug('primeval', 'globals.json loaded', {
          tracVis: window.tracVis,
          visitorCountry: window.visitorCountry,
          visitorRegion: window.visitorRegion,
          consentMode: tracking.consent_mode,
          hasUser: !!responseJson.current_user,
          csrfUpdated: !!responseJson.pageCsrf?.token
        });

        // Dispatch event for ConsentManager
        debug('primeval', 'Dispatching trackingConfigReady event');
        window.dispatchEvent(new CustomEvent('trackingConfigReady', {
          detail: tracking
        }));

        return {
          ...responseJson,
          shouldRedirectToLocale
        };
      } else {
        debug.warn('primeval', `globals.json returned status ${res.status}`);
      }
    } catch (err) {
      debug.error('primeval', 'Failed to fetch globals.json:', err);
    }

    // Fallback if fetch failed
    return {
      pageCsrf: window.pageCsrf,
      RMTREV: window.RMTREV,
      current_user: window.current_user,
      tracVis: window.tracVis,
      locale: locale || 'en-US',
      shouldRedirectToLocale
    };
  }

  // Invalid locale - let server handle redirect
  window.ErrorReporter?.warn('Page should not have loaded without a locale in url');
  console.error('[Primeval] Page should not have loaded without a locale in url');

  return {
    pageCsrf: window.pageCsrf,
    RMTREV: window.RMTREV,
    current_user: window.current_user,
    tracVis: window.tracVis,
    locale: '',
    shouldRedirectToLocale
  };
};
